import { domToCanvas } from 'modern-screenshot';

import type { DiagnosticsScreenshot, ScreenshotFormat } from './collectBundle';

const CAPTURE_ROOT_SELECTOR = '#root';
const MODAL_EXCLUDE_SELECTOR = '[data-component="modal"]';
// Hard ceiling on the whole capture. Sized against measured render times: a dense
// pipeline board (48 cards across 6 columns) takes 5-6s on a warm dev machine, and
// encoding adds ~150ms. The previous 10s was sized for a renderer that took ~300ms
// and leaves too little headroom on slower hardware — a capture that times out is
// reported as failed and the reporter gets no image at all.
const CAPTURE_TIMEOUT_MS = 20_000;
// Budget for the renderer's own asset fetches (avatars, fonts). Kept below
// CAPTURE_TIMEOUT_MS so a slow remote asset degrades that one asset rather than
// losing the whole capture to the hard timeout above.
const ASSET_TIMEOUT_MS = 8_000;
export const MAX_ENCODE_ATTEMPTS = 6;
const INITIAL_QUALITY = 0.85;
const MIN_QUALITY = 0.4;
const QUALITY_STEP = 0.15;
const SCALE_STEP = 0.8;

export type ScreenshotCaptureResult =
  | { status: 'included'; screenshot: DiagnosticsScreenshot }
  | { status: 'unsupported' | 'failed' | 'too_large' };

function toBlobAsync(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number
): Promise<Blob | null> {
  return new Promise(resolve => canvas.toBlob(b => resolve(b), type, quality));
}

function readAsDataUri(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/**
 * Drops the report modal itself from the capture. react-aria-components portals
 * the Dialog and its scrim to document.body, so they normally sit OUTSIDE #root
 * already — this is belt-and-braces for any future in-tree dialog. Excluding a
 * node excludes its subtree, which is what we want here.
 */
function isNotReportModal(node: Node): boolean {
  return !(
    node instanceof Element &&
    typeof node.matches === 'function' &&
    node.matches(MODAL_EXCLUDE_SELECTOR)
  );
}

function formatFromMimeType(mimeType: string): ScreenshotFormat {
  if (mimeType === 'image/webp') {
    return 'webp';
  }
  if (mimeType === 'image/jpeg') {
    return 'jpeg';
  }
  return 'png';
}

export async function captureDiagnosticsScreenshot(
  maxImageBytes: number
): Promise<ScreenshotCaptureResult> {
  if (
    typeof document === 'undefined' ||
    document.getElementById('root') === null ||
    typeof HTMLCanvasElement === 'undefined' ||
    typeof HTMLCanvasElement.prototype.toBlob !== 'function'
  ) {
    return { status: 'unsupported' };
  }

  if (maxImageBytes <= 0) {
    return { status: 'too_large' };
  }

  let captureTimer: number | undefined;

  try {
    const rootEl = document.querySelector(CAPTURE_ROOT_SELECTOR) as HTMLElement;

    // Runs the whole pipeline — render, then the encode loop — as a single unit so
    // the Promise.race below (and CAPTURE_TIMEOUT_MS) bounds all of it, not just the
    // domToCanvas call. A hung canvas.toBlob/FileReader callback must not be able to
    // hang the capture past the documented hard ceiling.
    const runCapture = async (): Promise<ScreenshotCaptureResult> => {
      // Known fidelity limits, both acceptable for support triage:
      //   - Scroll containers render from their origin, so the capture shows the top
      //     of a scrolled list rather than the region the reporter was looking at.
      //   - Cross-origin images (e.g. Google avatars) may not resolve and render blank.
      // Do NOT swap this for @zumer/snapdom: its scroll-offset emulation wraps a
      // scrolled container's children in an inline-block div, which destroys any flex
      // or grid formatting context and renders the whole subtree blank — that silently
      // erased the pipeline board from real customer reports.
      const srcCanvas = await domToCanvas(rootEl, {
        filter: isNotReportModal,
        timeout: ASSET_TIMEOUT_MS,
      });

      let scale = 1;
      let quality = INITIAL_QUALITY;

      for (let attempt = 0; attempt < MAX_ENCODE_ATTEMPTS; attempt++) {
        const w = Math.max(1, Math.round(srcCanvas.width * scale));
        const h = Math.max(1, Math.round(srcCanvas.height * scale));

        const offscreen = document.createElement('canvas');
        offscreen.width = w;
        offscreen.height = h;
        const ctx = offscreen.getContext('2d');
        ctx?.drawImage(srcCanvas, 0, 0, w, h);

        // canvas.toBlob silently substitutes png for an unsupported webp/jpeg mime type, so we
        // read the returned blob's actual `type` to learn what format we really got back.
        let blob = await toBlobAsync(offscreen, 'image/webp', quality);
        if (blob === null || blob.type !== 'image/webp') {
          blob = await toBlobAsync(offscreen, 'image/jpeg', quality);
          if (blob === null || blob.type !== 'image/jpeg') {
            blob = await toBlobAsync(offscreen, 'image/png', quality);
          }
        }

        if (blob !== null && blob.size <= maxImageBytes) {
          const dataUri = await readAsDataUri(blob);
          return {
            status: 'included',
            screenshot: {
              dataUri,
              format: formatFromMimeType(blob.type),
              byteLength: blob.size,
              width: w,
              height: h,
            },
          };
        }

        quality = Math.max(MIN_QUALITY, quality - QUALITY_STEP);
        scale = scale * SCALE_STEP;
      }

      return { status: 'too_large' };
    };

    const capturePromise = runCapture();
    // If the timeout wins the race below, capturePromise keeps running in the
    // background with nothing awaiting it — attach a no-op handler so a late
    // rejection doesn't surface as an unhandled promise rejection.
    capturePromise.catch(() => {});
    const timeoutPromise = new Promise<never>((_, reject) => {
      // cast: DOM setTimeout returns number; Node types add Timeout — use number directly
      captureTimer = setTimeout(
        () => reject(new Error('capture timed out')),
        CAPTURE_TIMEOUT_MS
      ) as unknown as number;
    });
    const result = await Promise.race([capturePromise, timeoutPromise]);
    clearTimeout(captureTimer);
    return result;
  } catch {
    clearTimeout(captureTimer);
    return { status: 'failed' };
  }
}
